/**
 * The job matching logo's arc runs around the ring while the piece is on screen: its head leaves the
 * sparks and runs out over the first 38 frames of the composition's 60 (25 fps), its tail follows
 * from frame 17 to 60, and the cycle ends with the ring empty again, so the loop shows no seam. The
 * sparks above the tree are their own piece (sparks.js).
 *
 * The arc is a dash on the ring: its length and its offset are the two main-thread properties here,
 * and they tick only while the piece runs on screen (modules/motion/onScreen.js). A piece that is
 * not playing (job matching off) rests on the tree and the sparks.
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {playOnScreen} from '../../modules/motion/onScreen.js';

const SELECTOR = Object.freeze({
    root: '.js-motionJobMatchingLogo',
    ring: '.js-motionJobMatchingLogoRing',
});

/** Milliseconds of one cycle. */
const CYCLE = 2400;

/** Share of the ring the tail runs, and the frame of the cycle it leaves at. */
const TAIL = Object.freeze({share: 0.89, leavesAt: 17 / 60});

/**
 * The arc's length every second frame, as a share of the ring: what lies between its head, which runs
 * out on the step curve, and its tail, which follows on a curve of its own.
 */
const ARC_LENGTH =
    'linear(0, 0.009, 0.046, 0.144, 0.323, 0.478, 0.583, 0.657, 0.712, 0.754, 0.782, 0.798, 0.802, 0.794, 0.771, 0.733, 0.678, 0.604, 0.514, 0.416, 0.323, 0.243, 0.177, 0.126, 0.086, 0.056, 0.033, 0.017, 0.007, 0.001, 0)';

/* The composition's tail curve, to the nearest named one. */
const EASE_TAIL = EASING_CURVES['ease-in-out-cubic'];

/** @type {WeakMap<HTMLElement, import('../../modules/motion/onScreen.js').Play>} */
const plays = new WeakMap();

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    const ring = root.querySelector(SELECTOR.ring);
    if (!(ring instanceof SVGElement)) {
        return;
    }
    const timing = {duration: CYCLE, iterations: Infinity};
    run.add(
        ring.animate([{strokeDasharray: '0 1'}, {strokeDasharray: '1 1'}], {...timing, easing: ARC_LENGTH}),
        ring.animate([{strokeDashoffset: 0}, {strokeDashoffset: 0, offset: TAIL.leavesAt, easing: EASE_TAIL}, {strokeDashoffset: -TAIL.share}], timing)
    );
}

/**
 * Turn the piece on or off: on, it runs while it is on screen; off, it rests.
 * @param {HTMLElement} root
 * @param {boolean} playing
 */
export function setPlaying(root, playing) {
    plays.get(root)?.stop();
    plays.delete(root);
    root.toggleAttribute('data-playing', playing);
    if (playing) {
        plays.set(
            root,
            playOnScreen(root, (run) => start(root, run))
        );
    }
}

/**
 * Initialize every piece that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement && root.hasAttribute('data-playing') && !plays.has(root)) {
            setPlaying(root, true);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
